import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import LicenseTable from "@/components/licenses/license-table";
import SimpleAddLicenseDialog from "@/components/licenses/simple-add-license-dialog";
import RenewalCalendar from "@/components/licenses/renewal-calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { exportToCsv, parseCSV } from "@/lib/utils";
import { License } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { getUserFriendlyError, userMessages } from "@/lib/user-friendly-messages";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription, 
  DialogFooter,
  DialogClose
} from "@/components/ui/dialog";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Loader2, Upload, Download, Plus, Filter, CheckCircle } from "lucide-react";

export default function Licenses() {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [importData, setImportData] = useState<Record<string, string>[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importErrors, setImportErrors] = useState<any[]>([]);
  const [importStep, setImportStep] = useState<'upload' | 'map' | 'validate' | 'review' | 'import'>('upload');
  const [importProgress, setImportProgress] = useState<number>(0);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [duplicateWarnings, setDuplicateWarnings] = useState<any[]>([]);
  const [validateResults, setValidateResults] = useState<{valid: any[], invalid: any[]}>({valid: [], invalid: []});
  const [isSyncingSeats, setIsSyncingSeats] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Filter settings
  const [filters, setFilters] = useState({
    status: "all",
    billingCycle: "all",
    costMin: "",
    costMax: "",
    seatsMin: "",
    seatsMax: "",
    expiringWithin: "all",
    vendors: [] as string[],
    employeeId: "",
    productOwnerId: ""
  });
  
  // Track when filters are applied
  const [activeFilters, setActiveFilters] = useState(filters);

  // Get all licenses for export
  const { data: licenses = [] } = useQuery<License[]>({
    queryKey: ['/api/licenses'],
  });
  
  // Get all applications for mapping names to licenses
  const { data: applications = [] } = useQuery<any[]>({
    queryKey: ['/api/applications'],
  });

  // Get employees for the "Assigned To" filter
  const { data: employees = [] } = useQuery<any[]>({
    queryKey: ['/api/employees'],
  });

  // Mutation for bulk import
  const bulkImportMutation = useMutation({
    mutationFn: async (licenses: any) => {
      const res = await apiRequest("POST", "/api/licenses/bulk", { licenses: licenses });
      if (!res.ok) {
        const error = await res.json();
        const errorMessage = getUserFriendlyError(error);
        throw new Error(errorMessage.description);
      }
      return await res.json();
    }
  });

  // Handle file upload for CSV import
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset any previous import state
    setImportErrors([]);
    setDuplicateWarnings([]);
    setValidateResults({valid: [], invalid: []});
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const parsedData = parseCSV(text);
        
        if (parsedData.length === 0) {
          toast({
            title: "Empty CSV file",
            description: "The uploaded file contains no data rows.",
            variant: "destructive",
          });
          return;
        }
        
        // Extract headers from the first row
        const headers = Object.keys(parsedData[0]);
        setCsvHeaders(headers);
        
        // Initialize field mapping with best guess matches
        const initialMapping: Record<string, string> = {};
        const targetFields = [
          'name', 'description', 'applicationName', 'applicationId',
          'billingCycle', 'cost', 'currency', 'startDate', 'renewalDate',
          'status', 'totalSeats', 'assignedSeats'
        ];

        // Sort by length descending so more specific fields (e.g. "applicationName")
        // are matched before shorter ones (e.g. "name") — prevents "Application Name"
        // header from wrongly mapping to the license "name" field.
        const sortedFields = [...targetFields].sort((a, b) => b.length - a.length);

        // Track which target fields have already been mapped to avoid duplicates
        const mappedTargets = new Set<string>();

        headers.forEach(header => {
          const normalizedHeader = header.toLowerCase().replace(/[^a-z0-9]/g, '');

          // 1. Prefer exact match first
          let match = sortedFields.find(field =>
            !mappedTargets.has(field) && normalizedHeader === field.toLowerCase()
          );

          // 2. Fall back to contains match (longer fields matched first via sort)
          if (!match) {
            match = sortedFields.find(field => {
              if (mappedTargets.has(field)) return false;
              const normalizedField = field.toLowerCase();
              return normalizedHeader.includes(normalizedField) || normalizedField.includes(normalizedHeader);
            });
          }

          if (match) {
            initialMapping[header] = match;
            mappedTargets.add(match);
          }
        });
        
        setFieldMapping(initialMapping);
        setImportData(parsedData);
        setImportStep('map');
        setIsBulkImportOpen(true);
      } catch (error) {
        const errorMessage = getUserFriendlyError(error);
        toast({
          title: errorMessage.title,
          description: errorMessage.description,
          variant: errorMessage.variant,
        });
      }
    };
    reader.readAsText(file);
    // Reset file input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle export to CSV
  const handleExport = () => {
    if (licenses.length === 0) {
      toast({
        title: "No data to export",
        description: "There are no licenses to export.",
        variant: "destructive",
      });
      return;
    }

    // Create an enhanced dataset with application names
    const enhancedLicenses = licenses.map(license => {
      const application = applications.find(app => app.id === license.applicationId);
      return {
        ...license,
        applicationName: application?.name || 'Unknown Application'
      };
    });

    // Define which fields to include in the export
    const headers = [
      'name',
      'description',
      'applicationId',
      'applicationName', // Added application name
      'licenseType',
      'billingCycle',
      'cost',
      'currency',
      'startDate',
      'renewalDate',
      'status',
      'totalSeats',
      'assignedSeats'
    ];

    exportToCsv(enhancedLicenses, 'licenses_export', headers);
    toast({
      title: "Export successful",
      description: `Successfully exported ${licenses.length} licenses with application names.`,
    });
  };

  // Apply field mapping to transform the data
  const applyFieldMapping = () => {
    // Validate required fields are mapped
    const requiredFields = ['name', 'applicationName'];
    const missingRequiredFields = requiredFields.filter(field => 
      !Object.values(fieldMapping).includes(field)
    );
    
    if (missingRequiredFields.length > 0) {
      toast({
        title: "Missing required fields",
        description: `Please map the following required fields: ${missingRequiredFields.join(', ')}`,
        variant: "destructive",
      });
      return false;
    }
    
    // Transform data based on field mapping
    const transformedData = importData.map(row => {
      const transformedRow: Record<string, any> = {};
      
      // Apply mappings
      Object.entries(fieldMapping).forEach(([csvField, targetField]) => {
        if (row[csvField] !== undefined) {
          transformedRow[targetField] = row[csvField];
        }
      });
      
      return transformedRow;
    });
    
    // Validate application names exist
    let validRecords = [];
    let invalidRecords = [];
    
    for (const record of transformedData) {
      if (!record.applicationName) {
        invalidRecords.push({
          ...record,
          error: 'Missing application name'
        });
        continue;
      }
      
      // Find the application by name (case-insensitive)
      const matchingApp = applications.find(app => 
        app.name.toLowerCase() === record.applicationName.toLowerCase()
      );
      
      if (matchingApp) {
        // Replace applicationName with applicationId for existing app
        record.applicationId = matchingApp.id;
        validRecords.push(record);
      } else {
        // New application will be created automatically
        // Mark application as new and include in valid records
        validRecords.push({
          ...record,
          newApplication: true,
          // Basic app details derived from the license data
          applicationDetails: {
            name: record.applicationName,
            status: "active",
            category: "Other", // Default category
            vendor: record.applicationName.split(' ')[0] || "Unknown", // Use first word as vendor or "Unknown"
          }
        });
      }
    }
    
    setValidateResults({
      valid: validRecords,
      invalid: invalidRecords
    });
    
    setImportStep('validate');
    return true;
  };
  
  // Handle moving to the next step in the import process
  const handleNextStep = () => {
    if (importStep === 'map') {
      applyFieldMapping();
    } else if (importStep === 'validate') {
      setImportStep('review');
    }
  };
  
  // Handle going back in the import wizard
  const handleBackStep = () => {
    if (importStep === 'map') {
      setImportStep('upload');
      setFieldMapping({});
    } else if (importStep === 'validate') {
      setImportStep('map');
    } else if (importStep === 'review') {
      setImportStep('validate');
    }
  };
  
  // Reset import state
  const resetImportState = () => {
    setImportStep('upload');
    setImportData([]);
    setFieldMapping({});
    setCsvHeaders([]);
    setValidateResults({valid: [], invalid: []});
    setImportErrors([]);
    setImportProgress(0);
    setIsImporting(false);
    setDuplicateWarnings([]);
  };

  // Handle bulk import
  const handleBulkImport = () => {
    if (validateResults.valid.length === 0) {
      toast({
        title: "No valid data to import",
        description: "There are no valid licenses to import.",
        variant: "destructive",
      });
      return;
    }

    setIsImporting(true);
    setImportStep('import');
    setImportProgress(0);
    
    // Start the import process
    const importInterval = setInterval(() => {
      setImportProgress(prev => {
        if (prev >= 90) {
          clearInterval(importInterval);
          return prev;
        }
        return prev + 10;
      });
    }, 300);
    
    bulkImportMutation.mutate(validateResults.valid, {
      onSuccess: (data) => {
        clearInterval(importInterval);
        setImportProgress(100);
        
        // Update applications list after import (since new ones might have been created)
        queryClient.invalidateQueries({ queryKey: ['/api/applications'] });
        
        setTimeout(() => {
          setIsBulkImportOpen(false);
          resetImportState();
          
          toast({
            title: "Import successful",
            description: `Successfully imported ${data.licenses?.length || validateResults.valid.length} licenses.`,
          });
        }, 1500);
      },
      onError: (error: Error) => {
        clearInterval(importInterval);
        setImportProgress(0);
        const errorMessage = getUserFriendlyError(error);
        setImportErrors([{ message: errorMessage.description }]);
        setImportStep('validate');
        
        toast({
          title: errorMessage.title,
          description: errorMessage.description,
          variant: errorMessage.variant,
        });
      }
    });
  };

  // Handle seat synchronization
  const handleSyncSeats = async () => {
    setIsSyncingSeats(true);
    try {
      const response = await fetch("/api/licenses/sync-seats", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });
      
      const result = await response.json();
      
      if (response.ok) {
        toast({
          title: "Seat synchronization complete",
          description: "All license seat allocations have been fixed and synchronized.",
        });
        
        // Refresh the licenses list to show updated seat counts
        queryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
      } else {
        toast({
          title: "Synchronization failed",
          description: result.message || "Failed to synchronize seat allocations",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to synchronize seat allocations",
        variant: "destructive",
      });
    } finally {
      setIsSyncingSeats(false);
    }
  };

  // Trigger file input click
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Download template CSV
  const downloadTemplate = () => {
    const today = new Date();
    const y1 = today.toISOString().split('T')[0];
    const addMonths = (d: Date, m: number) => { const x = new Date(d); x.setMonth(x.getMonth() + m); return x.toISOString().split('T')[0]; };

    const templateData = [
      { applicationName: "Microsoft 365", name: "Microsoft 365 Business", totalSeats: "50", cost: "6000", billingCycle: "Yearly", startDate: y1, renewalDate: addMonths(today, 12), status: "active", assignedToEmployee: "john@company.com", assignedTeam: "All Staff", paymentCard: "Corporate Card", currency: "USD", description: "Office suite for the entire org" },
      { applicationName: "Slack", name: "Slack Pro", totalSeats: "80", cost: "960", billingCycle: "Yearly", startDate: y1, renewalDate: addMonths(today, 12), status: "active", assignedToEmployee: "ops@company.com", assignedTeam: "Engineering", paymentCard: "Corporate Card", currency: "USD", description: "Team messaging platform" },
      { applicationName: "Adobe Creative Cloud", name: "Creative Cloud All Apps", totalSeats: "10", cost: "5994", billingCycle: "Yearly", startDate: y1, renewalDate: addMonths(today, 12), status: "active", assignedToEmployee: "design@company.com", assignedTeam: "Design Team", paymentCard: "Design Card", currency: "USD", description: "Full creative suite for design team" },
      { applicationName: "Salesforce", name: "Salesforce Enterprise", totalSeats: "25", cost: "37500", billingCycle: "Yearly", startDate: y1, renewalDate: addMonths(today, 12), status: "active", assignedToEmployee: "sales@company.com", assignedTeam: "Sales Team", paymentCard: "Sales Card", currency: "USD", description: "CRM platform" },
      { applicationName: "Zoom", name: "Zoom Business", totalSeats: "100", cost: "1990", billingCycle: "Yearly", startDate: y1, renewalDate: addMonths(today, 12), status: "active", assignedToEmployee: "it@company.com", assignedTeam: "All Staff", paymentCard: "Corporate Card", currency: "USD", description: "Video conferencing" },
      { applicationName: "GitHub", name: "GitHub Team", totalSeats: "30", cost: "1188", billingCycle: "Yearly", startDate: y1, renewalDate: addMonths(today, 12), status: "active", assignedToEmployee: "dev@company.com", assignedTeam: "Engineering", paymentCard: "Tech Card", currency: "USD", description: "Source code management" },
      { applicationName: "Figma", name: "Figma Organization", totalSeats: "15", cost: "3600", billingCycle: "Yearly", startDate: y1, renewalDate: addMonths(today, 12), status: "active", assignedToEmployee: "design@company.com", assignedTeam: "Design Team", paymentCard: "Design Card", currency: "USD", description: "UI/UX design collaboration" },
      { applicationName: "Jira", name: "Jira Software", totalSeats: "40", cost: "4800", billingCycle: "Yearly", startDate: y1, renewalDate: addMonths(today, 12), status: "active", assignedToEmployee: "pm@company.com", assignedTeam: "Product Team", paymentCard: "Corporate Card", currency: "USD", description: "Project and issue tracking" },
      { applicationName: "HubSpot", name: "HubSpot Marketing Hub", totalSeats: "5", cost: "2400", billingCycle: "Monthly", startDate: y1, renewalDate: addMonths(today, 1), status: "active", assignedToEmployee: "marketing@company.com", assignedTeam: "Marketing Team", paymentCard: "Marketing Card", currency: "USD", description: "Inbound marketing platform" },
      { applicationName: "Notion", name: "Notion Team Plan", totalSeats: "60", cost: "1440", billingCycle: "Yearly", startDate: y1, renewalDate: addMonths(today, 12), status: "active", assignedToEmployee: "ops@company.com", assignedTeam: "All Staff", paymentCard: "Corporate Card", currency: "USD", description: "Knowledge base and documentation" },
    ];

    const headers = [
      'applicationName',  // Now first for clarity
      'name',
      'totalSeats',
      'cost',
      'billingCycle',
      'startDate',
      'renewalDate',
      'status',
      'assignedToEmployee',
      'assignedTeam',
      'paymentCard',
      'currency',
      'description'
    ];

    exportToCsv(templateData, 'licenses_template', headers);
    
    toast({
      title: "Template Downloaded",
      description: "Fill in your license data using the example format and import when ready. New applications will be created automatically.",
    });
  };
  
  // Check if any filters are active
  const isFiltersActive = () => {
    return (
      activeFilters.status !== "all" ||
      activeFilters.billingCycle !== "all" ||
      activeFilters.costMin !== "" ||
      activeFilters.costMax !== "" ||
      activeFilters.seatsMin !== "" ||
      activeFilters.seatsMax !== "" ||
      activeFilters.expiringWithin !== "all" ||
      activeFilters.vendors.length > 0 ||
      activeFilters.employeeId !== "" ||
      activeFilters.productOwnerId !== ""
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="mb-4 md:mb-0">
            <h2 className="text-2xl font-bold text-slate-800">License Management</h2>
            <p className="text-sm text-slate-500 mt-1">Track and manage all your SaaS licenses and subscriptions</p>
          </div>
          <div className="btn-group-mobile">
            <Button 
              variant={isFiltersActive() ? "default" : "outline"}
              size="sm" 
              onClick={() => setIsFilterDialogOpen(true)}
              className={isFiltersActive() ? "bg-blue-500 hover:bg-blue-600" : ""}
            >
              <Filter className="h-4 w-4 mr-2" />
              {isFiltersActive() ? "Filters Active" : "Filter"}
            </Button>
            
            <Button variant="outline" size="sm" onClick={handleExport} className="btn-mobile">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            
            <Button variant="outline" size="sm" asChild className="btn-mobile">
              <a href="/data-import">
                <Upload className="h-4 w-4 mr-2" />
                Import
              </a>
            </Button>
            
            <Button size="sm" onClick={() => setIsAddDialogOpen(true)} className="btn-mobile">
              <Plus className="h-4 w-4 mr-2" />
              Add License
            </Button>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleSyncSeats} 
              disabled={isSyncingSeats}
              className="btn-mobile"
            >
              {isSyncingSeats ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              {isSyncingSeats ? "Syncing..." : "Fix Seats"}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Active Filter Summary */}
      {isFiltersActive() && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium text-blue-700">Active Filters:</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-blue-600 hover:text-blue-800 hover:bg-blue-100"
              onClick={() => {
                setFilters({
                  status: "all",
                  billingCycle: "all",
                  costMin: "",
                  costMax: "",
                  seatsMin: "",
                  seatsMax: "",
                  expiringWithin: "all",
                  vendors: [],
                  employeeId: "",
                  productOwnerId: ""
                });
                setActiveFilters({
                  status: "all",
                  billingCycle: "all",
                  costMin: "",
                  costMax: "",
                  seatsMin: "",
                  seatsMax: "",
                  expiringWithin: "all",
                  vendors: [],
                  employeeId: "",
                  productOwnerId: ""
                });
                toast({
                  title: "Filters Reset",
                  description: "All filters have been cleared.",
                });
              }}
            >
              Clear All
            </Button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {activeFilters.status !== "all" && (
              <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex items-center">
                Status: {activeFilters.status}
                <CheckCircle className="h-3 w-3 ml-1" />
              </div>
            )}
            {activeFilters.billingCycle !== "all" && (
              <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex items-center">
                Billing: {activeFilters.billingCycle}
                <CheckCircle className="h-3 w-3 ml-1" />
              </div>
            )}
            {activeFilters.costMin && (
              <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex items-center">
                Min Cost: ${activeFilters.costMin}
                <CheckCircle className="h-3 w-3 ml-1" />
              </div>
            )}
            {activeFilters.costMax && (
              <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex items-center">
                Max Cost: ${activeFilters.costMax}
                <CheckCircle className="h-3 w-3 ml-1" />
              </div>
            )}
            {activeFilters.seatsMin && (
              <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex items-center">
                Min Seats: {activeFilters.seatsMin}
                <CheckCircle className="h-3 w-3 ml-1" />
              </div>
            )}
            {activeFilters.seatsMax && (
              <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex items-center">
                Max Seats: {activeFilters.seatsMax}
                <CheckCircle className="h-3 w-3 ml-1" />
              </div>
            )}
            {activeFilters.expiringWithin !== "all" && (
              <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex items-center">
                Expiring: {activeFilters.expiringWithin === "never" ? "Never" : `Next ${activeFilters.expiringWithin} days`}
                <CheckCircle className="h-3 w-3 ml-1" />
              </div>
            )}
            {activeFilters.vendors.length > 0 && (
              <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex items-center">
                Apps: {activeFilters.vendors.length} selected
                <CheckCircle className="h-3 w-3 ml-1" />
              </div>
            )}
            {activeFilters.employeeId && (
              <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex items-center">
                Person: {employees.find((e: any) => e.id.toString() === activeFilters.employeeId)?.fullName ?? activeFilters.employeeId}
                <CheckCircle className="h-3 w-3 ml-1" />
              </div>
            )}
            {activeFilters.productOwnerId && (
              <div className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full flex items-center">
                Owner: {employees.find((e: any) => e.id.toString() === activeFilters.productOwnerId)?.fullName ?? activeFilters.productOwnerId}
                <CheckCircle className="h-3 w-3 ml-1" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs 
        defaultValue="all" 
        className="mb-6"
        onValueChange={(value) => setActiveTab(value)}
      >
        <TabsList className="h-9 p-1">
          <TabsTrigger value="all" className="h-7 px-3 py-1 text-sm">All Licenses</TabsTrigger>
          <TabsTrigger value="active" className="h-7 px-3 py-1 text-sm">Active</TabsTrigger>
          <TabsTrigger value="expiring" className="h-7 px-3 py-1 text-sm">Expiring Soon</TabsTrigger>
          <TabsTrigger value="expired" className="h-7 px-3 py-1 text-sm text-red-600 data-[state=active]:text-red-700">Expired</TabsTrigger>
          <TabsTrigger value="discontinued" className="h-7 px-3 py-1 text-sm">Discontinued</TabsTrigger>
          <TabsTrigger value="calendar" className="h-7 px-3 py-1 text-sm">Renewal Calendar</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          <LicenseTable
            filterStatus={null}
            onAddLicense={() => setIsAddDialogOpen(true)}
            customFilters={activeFilters}
          />
        </TabsContent>

        <TabsContent value="active" className="mt-4">
          <LicenseTable
            filterStatus="active"
            onAddLicense={() => setIsAddDialogOpen(true)}
            customFilters={activeFilters}
          />
        </TabsContent>

        <TabsContent value="expiring" className="mt-4">
          <LicenseTable
            filterStatus="expiring"
            onAddLicense={() => setIsAddDialogOpen(true)}
            customFilters={activeFilters}
          />
        </TabsContent>

        <TabsContent value="expired" className="mt-4">
          <LicenseTable
            filterStatus="expired"
            onAddLicense={() => setIsAddDialogOpen(true)}
            customFilters={activeFilters}
          />
        </TabsContent>

        <TabsContent value="discontinued" className="mt-4">
          <LicenseTable
            filterStatus="discontinued"
            onAddLicense={() => setIsAddDialogOpen(true)}
            customFilters={activeFilters}
          />
        </TabsContent>

        <TabsContent value="calendar" className="mt-4">
          <RenewalCalendar />
        </TabsContent>
      </Tabs>
      
      {/* Add License Dialog */}
      <SimpleAddLicenseDialog 
        open={isAddDialogOpen} 
        onOpenChange={setIsAddDialogOpen}
      />
      
      {/* Bulk Import Dialog */}
      <Dialog open={isBulkImportOpen} onOpenChange={(open) => {
        if (!open) {
          // Reset import state when dialog is closed
          setImportStep('upload');
          setImportData([]);
          setFieldMapping({});
          setCsvHeaders([]);
          setValidateResults({valid: [], invalid: []});
          setImportErrors([]);
        }
        setIsBulkImportOpen(open);
      }}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Software License Import</DialogTitle>
            <DialogDescription>
              {importStep === 'upload' && "Upload a CSV file with your licenses data or download a template to get started."}
              {importStep === 'map' && "Map your CSV columns to the appropriate license fields."}
              {importStep === 'validate' && `Validation results: ${validateResults.valid.length} valid records, ${validateResults.invalid.length} invalid records.`}
              {importStep === 'review' && `Ready to import ${validateResults.valid.length} valid licenses. Review and confirm.`}
            </DialogDescription>
          </DialogHeader>
          
          {/* Step 1: Upload CSV File */}
          {importStep === 'upload' && (
            <div className="py-6 flex flex-col items-center gap-4">
              <div className="flex justify-center gap-4 w-full">
                <Button variant="outline" className="w-full" onClick={triggerFileInput}>
                  <Upload className="h-4 w-4 mr-2" />
                  Select CSV File
                </Button>
                <Button variant="outline" className="w-full" onClick={downloadTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  Download Template
                </Button>
              </div>
              <div className="text-sm text-slate-500 text-center mt-4">
                <p className="font-semibold mb-2">Required Fields:</p>
                <ul className="list-disc pl-5 mb-2">
                  <li>License name</li>
                  <li>Application name (must match an existing application)</li>
                </ul>
                <p className="font-semibold mb-2">Recommended Fields:</p>
                <ul className="list-disc pl-5">
                  <li>Billing cycle (monthly, annually, etc.)</li>
                  <li>Cost</li>
                  <li>Total seats</li>
                  <li>Start date</li>
                  <li>Renewal date</li>
                </ul>
              </div>
              <input
                type="file"
                accept=".csv"
                className="hidden"
                ref={fileInputRef}
                onChange={handleFileUpload}
              />
            </div>
          )}
          
          {/* Step 2: Map Fields */}
          {importStep === 'map' && (
            <div className="py-4">
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-700">
                  Map your CSV columns to the appropriate license fields. Required fields are marked with an asterisk (*).
                </p>
              </div>
              
              <div className="max-h-[300px] overflow-y-auto space-y-3 pb-4">
                {csvHeaders.map((header) => (
                  <div key={header} className="grid grid-cols-3 gap-4 items-center">
                    <div className="text-sm font-medium text-slate-700">{header}</div>
                    <div className="col-span-2">
                      <Select
                        value={fieldMapping[header] || ""}
                        onValueChange={(value) => {
                          setFieldMapping(prev => ({ ...prev, [header]: value }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select field" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">-- Skip this column --</SelectItem>
                          <SelectItem value="name">Name *</SelectItem>
                          <SelectItem value="description">Description</SelectItem>
                          <SelectItem value="applicationName">Application Name *</SelectItem>
                          <SelectItem value="billingCycle">Billing Cycle</SelectItem>
                          <SelectItem value="cost">Cost</SelectItem>
                          <SelectItem value="currency">Currency</SelectItem>
                          <SelectItem value="startDate">Start Date</SelectItem>
                          <SelectItem value="renewalDate">Renewal Date</SelectItem>
                          <SelectItem value="status">Status</SelectItem>
                          <SelectItem value="totalSeats">Total Seats</SelectItem>
                          <SelectItem value="assignedSeats">Assigned Seats</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <p className="text-sm text-amber-700 flex items-center">
                  <span className="font-semibold mr-1">Note:</span> 
                  Application names must match exactly with existing applications in the system.
                </p>
              </div>
            </div>
          )}
          
          {/* Step 3: Validation Results */}
          {importStep === 'validate' && (
            <div className="py-4">
              {validateResults.invalid.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-red-700 mb-2">Invalid Records ({validateResults.invalid.length})</h3>
                  <div className="max-h-[200px] overflow-y-auto border border-red-200 rounded-md">
                    <table className="w-full">
                      <thead className="bg-red-50 sticky top-0">
                        <tr className="border-b border-red-200">
                          <th className="p-2 text-left text-xs font-medium text-red-700">Row</th>
                          <th className="p-2 text-left text-xs font-medium text-red-700">Name</th>
                          <th className="p-2 text-left text-xs font-medium text-red-700">Application</th>
                          <th className="p-2 text-left text-xs font-medium text-red-700">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validateResults.invalid.map((item, index) => (
                          <tr key={index} className="border-b border-red-100">
                            <td className="p-2 text-sm text-slate-600">{index + 1}</td>
                            <td className="p-2 text-sm text-slate-600">{item.name || "—"}</td>
                            <td className="p-2 text-sm text-slate-600">{item.applicationName || "—"}</td>
                            <td className="p-2 text-sm text-red-600">{item.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {validateResults.valid.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-green-700 mb-2">Valid Records ({validateResults.valid.length})</h3>
                  <div className="max-h-[200px] overflow-y-auto border border-green-200 rounded-md">
                    <table className="w-full">
                      <thead className="bg-green-50 sticky top-0">
                        <tr className="border-b border-green-200">
                          <th className="p-2 text-left text-xs font-medium text-green-700">Name</th>
                          <th className="p-2 text-left text-xs font-medium text-green-700">Application</th>
                          <th className="p-2 text-left text-xs font-medium text-green-700">Billing Cycle</th>
                          <th className="p-2 text-left text-xs font-medium text-green-700">Cost</th>
                          <th className="p-2 text-left text-xs font-medium text-green-700">Total Seats</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validateResults.valid.map((item, index) => (
                          <tr key={index} className="border-b border-green-100">
                            <td className="p-2 text-sm text-slate-600">{item.name}</td>
                            <td className="p-2 text-sm text-slate-600">{item.applicationName}</td>
                            <td className="p-2 text-sm text-slate-600">{item.billingCycle || "—"}</td>
                            <td className="p-2 text-sm text-slate-600">{item.cost ? `${item.currency || 'USD'} ${item.cost}` : "—"}</td>
                            <td className="p-2 text-sm text-slate-600">{item.totalSeats || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Step 4: Review and Import */}
          {importStep === 'review' && (
            <div className="py-4">
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                <h3 className="text-sm font-medium text-green-700 mb-2 flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2" /> 
                  Ready to Import
                </h3>
                <p className="text-sm text-green-700">
                  {validateResults.valid.length} licenses are ready to be imported. Click "Import Licenses" to proceed.
                </p>
              </div>
              
              <div className="max-h-[250px] overflow-y-auto border rounded-md">
                <table className="w-full">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="border-b">
                      <th className="p-2 text-left text-xs font-medium text-slate-500">Name</th>
                      <th className="p-2 text-left text-xs font-medium text-slate-500">Application</th>
                      <th className="p-2 text-left text-xs font-medium text-slate-500">Cost</th>
                      <th className="p-2 text-left text-xs font-medium text-slate-500">Start Date</th>
                      <th className="p-2 text-left text-xs font-medium text-slate-500">Renewal Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validateResults.valid.map((item, index) => (
                      <tr key={index} className="border-b">
                        <td className="p-2 text-sm text-slate-600">{item.name}</td>
                        <td className="p-2 text-sm text-slate-600">{item.applicationName}</td>
                        <td className="p-2 text-sm text-slate-600">{item.cost ? `${item.currency || 'USD'} ${item.cost}` : "—"}</td>
                        <td className="p-2 text-sm text-slate-600">{item.startDate || "—"}</td>
                        <td className="p-2 text-sm text-slate-600">{item.renewalDate || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          <DialogFooter>
            {importStep !== 'upload' && (
              <Button variant="outline" onClick={handleBackStep}>
                Back
              </Button>
            )}
            
            <Button variant="outline" onClick={() => {
              setIsBulkImportOpen(false);
              setImportStep('upload');
              setImportData([]);
              setFieldMapping({});
              setCsvHeaders([]);
            }}>
              Cancel
            </Button>
            
            {importStep === 'map' && (
              <Button onClick={handleNextStep}>
                Validate Data
              </Button>
            )}
            
            {importStep === 'validate' && validateResults.valid.length > 0 && (
              <Button onClick={handleNextStep}>
                Continue to Review
              </Button>
            )}
            
            {importStep === 'review' && (
              <Button onClick={handleBulkImport} disabled={isImporting}>
                {isImporting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Import Licenses
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Filter Dialog */}
      <Dialog open={isFilterDialogOpen} onOpenChange={setIsFilterDialogOpen}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] sm:max-w-[500px] grid-rows-[auto,minmax(0,1fr),auto] gap-0 p-0">
          <DialogHeader className="px-6 pb-3 pt-6">
            <DialogTitle>Filter Licenses</DialogTitle>
            <DialogDescription>
              Select criteria to filter the licenses displayed in the table.
            </DialogDescription>
          </DialogHeader>
          
          <div className="min-h-0 overflow-y-auto overscroll-contain px-6 py-4">
            <div className="grid gap-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select 
                  value={filters.status} 
                  onValueChange={(value) => setFilters({...filters, status: value})}
                >
                  <SelectTrigger id="status">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="billingCycle">Billing Cycle</Label>
                <Select 
                  value={filters.billingCycle} 
                  onValueChange={(value) => setFilters({...filters, billingCycle: value})}
                >
                  <SelectTrigger id="billingCycle">
                    <SelectValue placeholder="All Cycles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Cycles</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly / Annual</SelectItem>
                    <SelectItem value="lifetime">Lifetime / One-Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Cost Range</Label>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex items-center space-x-2">
                  <Label htmlFor="costMin" className="w-10">Min</Label>
                  <Input
                    id="costMin"
                    type="number"
                    min={0}
                    placeholder="Min cost"
                    value={filters.costMin}
                    onChange={(e) => setFilters({...filters, costMin: e.target.value})}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Label htmlFor="costMax" className="w-10">Max</Label>
                  <Input
                    id="costMax"
                    type="number"
                    min={0}
                    placeholder="Max cost"
                    value={filters.costMax}
                    onChange={(e) => setFilters({...filters, costMax: e.target.value})}
                  />
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Seats Range</Label>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex items-center space-x-2">
                  <Label htmlFor="seatsMin" className="w-10">Min</Label>
                  <Input
                    id="seatsMin"
                    type="number"
                    min={0}
                    placeholder="Min seats"
                    value={filters.seatsMin}
                    onChange={(e) => setFilters({...filters, seatsMin: e.target.value})}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Label htmlFor="seatsMax" className="w-10">Max</Label>
                  <Input
                    id="seatsMax"
                    type="number"
                    min={0}
                    placeholder="Max seats"
                    value={filters.seatsMax}
                    onChange={(e) => setFilters({...filters, seatsMax: e.target.value})}
                  />
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="expiringWithin">Expiring Within</Label>
              <Select 
                value={filters.expiringWithin} 
                onValueChange={(value) => setFilters({...filters, expiringWithin: value})}
              >
                <SelectTrigger id="expiringWithin">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="7">Next 7 days</SelectItem>
                  <SelectItem value="30">Next 30 days</SelectItem>
                  <SelectItem value="90">Next 90 days</SelectItem>
                  <SelectItem value="never">Never (Lifetime)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Applications</Label>
              <div className="max-h-[120px] overflow-y-auto border rounded-md p-2">
                {applications.length > 0 ? (
                  [...applications].sort((a: any, b: any) => a.name.localeCompare(b.name)).map((app: any) => (
                    <div key={app.id} className="flex items-center space-x-2 mb-2">
                      <Checkbox
                        id={`vendor-${app.id}`}
                        checked={filters.vendors.includes(app.name)}
                        onCheckedChange={(checked) => {
                          const newVendors = checked
                            ? [...filters.vendors, app.name]
                            : filters.vendors.filter(v => v !== app.name);
                          setFilters({...filters, vendors: newVendors});
                        }}
                      />
                      <Label htmlFor={`vendor-${app.id}`} className="text-sm font-normal">
                        {app.name}
                      </Label>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-slate-500 text-center py-2">
                    No applications available
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="employeeFilter">Assigned To (Person)</Label>
              <Select
                value={filters.employeeId}
                onValueChange={(value) => setFilters({...filters, employeeId: value === "all" ? "" : value})}
              >
                <SelectTrigger id="employeeFilter">
                  <SelectValue placeholder="Any person" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any person</SelectItem>
                  {[...employees].sort((a: any, b: any) => a.fullName.localeCompare(b.fullName)).map((emp: any) => (
                    <SelectItem key={emp.id} value={emp.id.toString()}>
                      {emp.fullName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="productOwnerFilter">Product Owner</Label>
              <Select
                value={filters.productOwnerId}
                onValueChange={(value) => setFilters({...filters, productOwnerId: value === "all" ? "" : value})}
              >
                <SelectTrigger id="productOwnerFilter">
                  <SelectValue placeholder="Any owner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any owner</SelectItem>
                  {[...employees].sort((a: any, b: any) => a.fullName.localeCompare(b.fullName)).map((emp: any) => (
                    <SelectItem key={emp.id} value={emp.id.toString()}>
                      {emp.fullName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            </div>
          </div>

          <DialogFooter className="flex-col gap-3 border-t bg-background px-6 py-4 sm:flex-row sm:justify-between sm:space-x-0">
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => {
                // Reset just the form values
                setFilters({
                  status: "all",
                  billingCycle: "all",
                  costMin: "",
                  costMax: "",
                  seatsMin: "",
                  seatsMax: "",
                  expiringWithin: "all",
                  vendors: [],
                  employeeId: "",
                  productOwnerId: ""
                });
              }} size="sm">
                Reset Form
              </Button>

              <Button variant="destructive" onClick={() => {
                // Reset filter form
                setFilters({
                  status: "all",
                  billingCycle: "all",
                  costMin: "",
                  costMax: "",
                  seatsMin: "",
                  seatsMax: "",
                  expiringWithin: "all",
                  vendors: [],
                  employeeId: "",
                  productOwnerId: ""
                });

                // Reset active filters too
                setActiveFilters({
                  status: "all",
                  billingCycle: "all",
                  costMin: "",
                  costMax: "",
                  seatsMin: "",
                  seatsMax: "",
                  expiringWithin: "all",
                  vendors: [],
                  employeeId: "",
                  productOwnerId: ""
                });

                // Close dialog
                setIsFilterDialogOpen(false);
                
                // Notify user
                toast({
                  title: "Filters Reset",
                  description: "All filters have been cleared.",
                });
              }} size="sm">
                Clear All
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 sm:justify-end">
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button 
                type="submit"
                onClick={() => {
                  // Apply filters
                  setActiveFilters(filters);
                  setIsFilterDialogOpen(false);
                  
                  // Notify user that filters were applied
                  toast({
                    title: "Filters Applied",
                    description: "The license list has been filtered according to your criteria.",
                  });
                }}
              >
                Apply Filters
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
